// noinspection ES6UnusedImports
import {Button, IconButton} from '@bonprix/design-system';
import Landmark from '@core/scripts/components/landmark-adapter';
import CoreMessages from '@core/scripts/components/message-list';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import React, {useEffect, useRef, useState} from 'react';
import WishlistMessages from '../messages';
import acquireWishlistState, {acquireWishlistStateByDigistyleId} from '../../helper/acquire-wishlist-state';
import addToWishlist from '../../helper/add-to-wishlist';
import removeFromWishlist from '../../helper/remove-from-wishlist';
import WishlistLandmarks from '../../helper/wishlist-landmarks';
import TrackingHelper from '@core/scripts/components/tracking/helper';
import {NOT_IN_TAB_BUT_IN_ARROW_ORDER} from '../../hooks/useGroupedFocus.js';

export default function WishlistHeart({
    iconButtonVariant,
    productCardItem,
    addLandmark,
    onAddSuccess = null,
    onAddError = null,
    onRemoveError = null,
    onRemoveSuccess = null,
    zgaMode = false,
    children,
    isMultiItem,
    label,
}) {
    const {digistyleId, articleNumber, itemId, sizeText} = productCardItem;
    const itemOptionId = productCardItem.getItemOptionId ?? undefined;

    const {
        promotion,
        decimalPrice,
        shopId,
        origin
    } = productCardItem.wishlistLandmarkData;

    const [isOnWishlist, setIsOnWishlist] = useState(false);
    const loading = useRef(false);
    const lastClick = useRef(new Date());
    const currentDigistyleId = useRef(digistyleId);
    const currentItemId = useRef(itemId);
    const fetchWishlistState = zgaMode ? acquireWishlistStateByDigistyleId : acquireWishlistState;


    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(WishlistMessages.WISHLIST.ITEM_DELETED, (wishlistRemovalPayload) => {
                const styleId = isMultiItem ? currentDigistyleId.current : digistyleId;
                const actualItemId = isMultiItem ? currentItemId.current : itemId;
                if(wishlistRemovalPayload.digistyleId === Number(styleId) &&
                    (zgaMode || wishlistRemovalPayload.itemId === Number(actualItemId))) {
                    setIsOnWishlist(false);
                }
            }),
            MessageBroker.subscribe(WishlistMessages.WISHLIST.ITEM_ADDED, (wishlistAdditionContext) => {
                const styleId = isMultiItem ? currentDigistyleId.current : digistyleId;
                const actualItemId = isMultiItem ? currentItemId.current : itemId;
                if(wishlistAdditionContext.digistyleId === Number(styleId) &&
                    (zgaMode || wishlistAdditionContext.itemId === Number(actualItemId))) {
                    setIsOnWishlist(true);
                }
            }),
        ];

        return () => subscriptions.forEach(subscription => MessageBroker.unsubscribe(subscription));
    }, []);

    useEffect(() => {
        loading.current = true;
        fetchWishlistState(digistyleId, itemId).then((onWishlist) => {
            setIsOnWishlist(onWishlist);
            loading.current = false;
        });
    }, []);

    const onClick = () => {
        if (loading.current || Date.now() - lastClick.current <= 500) {
            return;
        }
        lastClick.current = Date.now();
        loading.current = true;

        if (isOnWishlist) {
            removeArticleFromWishlist();
        } else {
            addArticleToWishlist();
        }
    };

    const addArticleToWishlist = () => {
        if (addLandmark != null) {
            Landmark.sendClickLandmark(addLandmark);
        }
        const actualStyleId = isMultiItem ? currentDigistyleId.current : digistyleId;
        const actualItemId = isMultiItem ? currentItemId.current : itemId;
        const eventId = TrackingHelper.generateUUID()
        addToWishlist(
            actualStyleId,
            shopId,
            actualItemId,
            articleNumber,
            itemOptionId,
            eventId
        ).then(() => {
            setIsOnWishlist(true);
            onAddSuccess?.();
            WishlistLandmarks.sendWishListItemAddLandMark({
                sizeText,
                articleNumber,
                promotion,
                decimalPrice,
                shopId,
                digistyleId: actualStyleId,
                itemId: actualItemId,
                itemOptionId,
                origin,
                eventId
            });
            loading.current = false;
        }).catch((error) => {
            MessageBroker.publish(CoreMessages.WISHLIST.SHOW_ERROR_LAYER, {error: error.message});
            onAddError?.(error.message);
            loading.current = false;
        });
    };

    const removeArticleFromWishlist = () => {
        const actualStyleId = isMultiItem ? currentDigistyleId.current : digistyleId;
        const actualItemId = isMultiItem ? currentItemId.current : itemId;
        removeFromWishlist(
            actualStyleId,
            zgaMode ? null : actualItemId
        ).then(() => {
            setIsOnWishlist(false);
            onRemoveSuccess?.();
            WishlistLandmarks.sendWishListItemRemoveLandMark({
                sizeText,
                articleNumber,
                promotion,
                decimalPrice,
                shopId,
                digistyleId: actualStyleId,
                itemId: actualItemId,
                itemOptionId,
                origin,
            });
            loading.current = false;
        }).catch(() => {
            onRemoveError?.();
            loading.current = false;
        });
    };

    const commonProps = {
        onClick,
        variant: iconButtonVariant,
        icon: isOnWishlist ? 'heart-full' : 'heart',
        iconPosition: 'end',
        fullWidth: true,
        tabIndex: NOT_IN_TAB_BUT_IN_ARROW_ORDER,
    };

    if (label) {
        // the IconButton from DeSy Cannot display a label so we need to use the normal button then
        return (
            <Button {...commonProps} label={label}>
                {label || children}
            </Button>
        );
    }

    return <IconButton {...commonProps}>{children}</IconButton>;
}
